import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import type { IContest } from '../../../types/global/helper';
import type { IAwardCategory } from '../../../redux/contest/types';
import type { ContestAiAssistContext, ContestWizardState } from '../contestWizard/types';
import { CategoryBuilderStep } from '../contestWizard/steps/CategoryBuilderStep';
import { CategoryAwardsStep } from '../contestWizard/steps/CategoryAwardsStep';
import { ContestAiAssistDialog, type ContestAiAssistResult } from '../contestWizard/ContestAiAssistDialog';
import { midnight } from '../contestWizard/midnightStyles';
import { showToastMessage } from '../../../utils/AppUtils';
import { reconcilePrizeStructureWithTiers } from '../contestWizard/awardPresets';
import {
    canProceedFromCategoryBuilder,
    createSessionWizardState,
    editModeWizardState,
    emptyWizardCategory,
    mergeCategoriesForSave,
    pruneEmptyCategories,
} from './contestCategoryWizardUtils';

export type SubmissionCategoryModalMode = 'create' | 'edit';

export interface BrandCreateSubmissionCategoryModalProps {
    open: boolean;
    mode: SubmissionCategoryModalMode;
    contest: IContest;
    awardCategories?: IAwardCategory[];
    editCategoryIndex?: number;
    saving?: boolean;
    onClose: () => void;
    onSave: (categories: Record<string, unknown>[], categoriesEnabled: boolean) => void | Promise<void>;
}

export function BrandCreateSubmissionCategoryModal({
    open,
    mode,
    contest,
    awardCategories,
    editCategoryIndex = 0,
    saving,
    onClose,
    onSave,
}: BrandCreateSubmissionCategoryModalProps) {
    const [step, setStep] = useState<0 | 1>(0);
    const [state, setState] = useState<ContestWizardState>(() =>
        mode === 'edit'
            ? editModeWizardState(contest.categories, awardCategories, editCategoryIndex)
            : createSessionWizardState(),
    );
    const [aiOpen, setAiOpen] = useState(false);
    const [aiContext, setAiContext] = useState<ContestAiAssistContext>('category');

    const idx = state.editingCategoryIndex ?? 0;

    useEffect(() => {
        if (!open) return;
        setStep(0);
        setState(
            mode === 'edit'
                ? editModeWizardState(contest.categories, awardCategories, editCategoryIndex)
                : createSessionWizardState(),
        );
    }, [open, mode, contest.categories, awardCategories, editCategoryIndex]);

    const patch = useCallback((p: Partial<ContestWizardState>) => {
        setState(prev => {
            const next = { ...prev, ...p };
            if (!next.categories?.length) {
                next.categories = [emptyWizardCategory()];
                next.editingCategoryIndex = 0;
            } else if ((next.editingCategoryIndex ?? 0) >= next.categories.length) {
                next.editingCategoryIndex = next.categories.length - 1;
            }
            return next;
        });
    }, []);

    const openAi = (ctx: ContestAiAssistContext) => {
        setAiContext(ctx);
        setAiOpen(true);
    };

    const applyAi = (result: ContestAiAssistResult) => {
        if (aiContext === 'category') {
            const cats = [...state.categories];
            const base = cats[idx] ?? cats[0];
            cats[idx] = {
                ...base,
                category_name: result.categoryName ?? base.category_name,
                category_description: result.categoryDescription ?? base.category_description,
                tier_fee:
                    result.tiers?.map(t => ({
                        label: t.label,
                        fee: t.fee,
                        currency: 'USD',
                    })) ?? base.tier_fee,
            };
            patch({ categories: cats, editingCategoryIndex: idx });
        } else if (aiContext === 'awards' && result.prize_structure?.length) {
            const cats = [...state.categories];
            if (cats[idx]) {
                cats[idx] = { ...cats[idx], prize_structure: result.prize_structure };
                patch({ categories: cats, editingCategoryIndex: idx });
            }
        }
        showToastMessage('AI draft applied — review and edit as needed', 'info');
    };

    const canContinueStep0 = useMemo(
        () => canProceedFromCategoryBuilder(state.categories),
        [state.categories],
    );

    const handleContinue = async () => {
        if (step === 0) {
            if (!canContinueStep0) return;
            const pruned = pruneEmptyCategories(state.categories).map(reconcilePrizeStructureWithTiers);
            const nextIdx = Math.min(state.editingCategoryIndex ?? 0, Math.max(0, pruned.length - 1));
            patch({ categories: pruned, editingCategoryIndex: nextIdx });
            setStep(1);
            return;
        }
        const pruned = pruneEmptyCategories(state.categories);
        const saveIdx = mode === 'edit' ? editCategoryIndex : Math.min(state.editingCategoryIndex ?? 0, pruned.length - 1);
        const payload = mergeCategoriesForSave(mode, contest.categories, pruned, saveIdx);
        const enableCategories = !contest.categories_enabled || payload.length > 0;
        try {
            await onSave(payload, enableCategories);
        } catch {
            /* parent onSave handles toast; keep modal open */
        }
    };

    const handleBack = () => {
        if (step === 1) setStep(0);
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={saving ? undefined : onClose}
                fullWidth
                maxWidth="lg"
                scroll="paper"
                PaperProps={{
                    sx: {
                        bgcolor: midnight.surface,
                        backgroundImage: 'none',
                        borderRadius: { xs: 0, sm: '20px' },
                        border: '1px solid rgba(255,255,255,0.08)',
                        maxHeight: { xs: '100%', sm: '92vh' },
                        m: { xs: 0, sm: 2 },
                    },
                }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        px: { xs: 2, md: 3 },
                        pt: 1.5,
                        pb: 0,
                    }}>
                    <IconButton
                        onClick={onClose}
                        disabled={saving}
                        aria-label="Close"
                        sx={{ color: midnight.onSurfaceVariant }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box
                    sx={{
                        px: { xs: 2, md: 3 },
                        py: 2,
                        overflow: 'auto',
                        flex: 1,
                        maxHeight: { xs: 'calc(100vh - 180px)', sm: 'calc(92vh - 200px)' },
                    }}>
                    {step === 0 ? (
                        <CategoryBuilderStep
                            state={state}
                            onChange={patch}
                            onOpenAiAssist={() => openAi('category')}
                        />
                    ) : (
                        <CategoryAwardsStep
                            state={{ ...state, categories_enabled: true }}
                            onChange={patch}
                            onOpenAiAssist={() => openAi('awards')}
                        />
                    )}
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: 2,
                        px: { xs: 2, md: 3 },
                        py: 2.5,
                        borderTop: '1px solid rgba(72,72,71,0.35)',
                    }}>
                    {step === 1 ? (
                        <Button
                            startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 14 }} />}
                            onClick={handleBack}
                            disabled={saving}
                            sx={{ color: midnight.onSurfaceVariant, fontWeight: 700 }}>
                            Back
                        </Button>
                    ) : (
                        <Box />
                    )}
                    <Button
                        variant="contained"
                        disabled={saving || (step === 0 && !canContinueStep0)}
                        onClick={handleContinue}
                        sx={{
                            bgcolor: midnight.primary,
                            color: '#392700',
                            fontWeight: 800,
                            px: 4,
                            py: 1.25,
                            borderRadius: '12px',
                            '&:hover': { bgcolor: '#e6b55a' },
                            '&.Mui-disabled': { bgcolor: 'rgba(255,201,101,0.35)', color: 'rgba(57,39,0,0.5)' },
                        }}>
                        {saving ? (
                            <CircularProgress size={22} sx={{ color: '#392700' }} />
                        ) : step === 0 ? (
                            'Continue to awards'
                        ) : mode === 'edit' ? (
                            'Save category'
                        ) : (
                            'Create category'
                        )}
                    </Button>
                </Box>
            </Dialog>

            <ContestAiAssistDialog
                open={aiOpen}
                context={aiContext}
                submissionState={contest.submission_state === 'private_submission' ? 'private_submission' : 'public_submission'}
                awardSourceCategory={
                    state.categories[state.editingCategoryIndex ?? 0] ?? state.categories[0]
                }
                onClose={() => setAiOpen(false)}
                onApply={applyAi}
            />
        </>
    );
}
